import { forwardRef, type CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import type { DocumentModel, DocumentPaymentLine, DocumentTone } from './types';

/**
 * A4 customer-facing document (quotation / tax invoice).
 *
 * Colour note: this is a paper document, not app chrome. It must render
 * identically in light and dark mode, in the print iframe and on the public
 * signing page, so it deliberately uses fixed ink/paper values plus the
 * tenant's brand colour instead of the app's theme tokens.
 *
 * `variant="print"` locks the sheet to 210mm for react-to-print;
 * `variant="screen"` lets it flow to the container width (public signing page,
 * mobile).
 */

interface DocumentSheetProps {
  doc: DocumentModel;
  variant?: 'print' | 'screen';
  className?: string;
}

const INK = '#141822';
const INK_MUTED = '#5c6372';
const INK_FAINT = '#8c929f';
const RULE = '#e3e7ec';
const PAPER_TINT = '#f6f8fa';

const TONE: Record<DocumentTone, { fg: string; bg: string }> = {
  neutral: { fg: '#5c6372', bg: '#eef1f4' },
  info: { fg: '#0a6fa8', bg: '#e6f3fb' },
  success: { fg: '#1c8742', bg: '#e6f5ea' },
  warning: { fg: '#9a5f00', bg: '#fdf3e0' },
  danger: { fg: '#b93232', bg: '#fbe9e9' },
};

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return `rgba(47, 88, 200, ${alpha})`;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
}

function Eyebrow({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <p
      className="text-[10px] font-semibold uppercase tracking-[0.08em] mb-1.5"
      style={{ color: color ?? INK_FAINT }}
    >
      {children}
    </p>
  );
}

function ContactLine({ label, value, href }: { label: string; value: string; href?: string }) {
  return (
    <p className="text-[12px] leading-5" style={{ color: INK_MUTED }}>
      <span className="inline-block w-10" style={{ color: INK_FAINT }}>{label}</span>
      {href ? <a href={href} className="no-underline" style={{ color: INK_MUTED }}>{value}</a> : value}
    </p>
  );
}

export const DocumentSheet = forwardRef<HTMLDivElement, DocumentSheetProps>(
  ({ doc, variant = 'print', className }, ref) => {
    const brand = doc.company.primaryColor;
    const brandTint = hexToRgba(brand, 0.07);
    const brandRule = hexToRgba(brand, 0.28);
    const money = doc.formatMoney;
    const isInvoice = doc.kind === 'invoice';
    const paymentLines: DocumentPaymentLine[] = isInvoice
      ? doc.totals.payments ?? ((doc.totals.paid ?? 0) > 0 ? [{ label: 'Paid', amount: doc.totals.paid ?? 0 }] : [])
      : [];
    const showBalance = isInvoice && doc.totals.balanceDue != null && paymentLines.length > 0;
    const balanceDue = doc.totals.balanceDue ?? doc.totals.total;
    const isPaid = isInvoice && doc.status?.tone === 'success';
    // US Letter is 216 × 279 mm; everything else prints on A4 (210 × 297 mm).
    const letter = doc.paper === 'Letter';

    const sheetStyle: CSSProperties = {
      color: INK,
      backgroundColor: '#ffffff',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      fontFeatureSettings: '"tnum" 1',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'document-sheet relative mx-auto font-sans antialiased',
          variant === 'print'
            ? cn(
                letter ? 'w-[216mm] min-h-[279mm]' : 'w-[210mm] min-h-[297mm]',
                'px-[14mm] pt-[12mm] pb-[14mm] shadow-lg print:shadow-none',
              )
            : cn(letter ? 'max-w-[216mm]' : 'max-w-[210mm]', 'w-full px-5 py-6 sm:px-10 sm:py-9 rounded-xl shadow-sm'),
          className,
        )}
        style={sheetStyle}
      >
        {/* Brand bar */}
        <div
          className={cn('absolute left-0 right-0 top-0 h-1.5', variant === 'screen' && 'rounded-t-xl')}
          style={{ backgroundColor: brand }}
        />

        {/* Rubber stamp */}
        {doc.stamp && (
          <div
            aria-hidden
            className="pointer-events-none absolute right-[14mm] top-[38mm] select-none rounded-md border-[3px] px-3 py-1 text-[22px] font-bold uppercase tracking-[0.18em] opacity-80"
            style={{
              color: TONE[doc.stamp.tone].fg,
              borderColor: TONE[doc.stamp.tone].fg,
              transform: 'rotate(-12deg)',
            }}
          >
            {doc.stamp.label}
          </div>
        )}

        {/* Header */}
        <header className="flex items-start justify-between gap-6 avoid-break">
          <div className="min-w-0 max-w-[58%]">
            {doc.company.logoUrl ? (
              <img
                src={doc.company.logoUrl}
                alt={doc.company.displayName}
                className="mb-3 h-14 max-w-[220px] object-contain object-left"
              />
            ) : (
              <p className="mb-2 text-[22px] font-bold leading-tight tracking-tight" style={{ color: brand }}>
                {doc.company.displayName}
              </p>
            )}
            <div className="space-y-0.5 text-[12px] leading-5" style={{ color: INK_MUTED }}>
              {doc.company.logoUrl && (
                <p className="font-semibold" style={{ color: INK }}>{doc.company.displayName}</p>
              )}
              {doc.company.legalName && <p>{doc.company.legalName}</p>}
              {doc.company.addressLines.map((line) => <p key={line}>{line}</p>)}
              {doc.company.identifiers.length > 0 && (
                <p>{doc.company.identifiers.map((i) => `${i.label} ${i.value}`).join('  ·  ')}</p>
              )}
            </div>
          </div>

          <div className="shrink-0 text-right">
            <h1
              className="text-[28px] font-bold uppercase leading-none tracking-tight"
              style={{ color: brand }}
            >
              {doc.title}
            </h1>
            <p className="mt-2 text-[15px] font-semibold tracking-wide" style={{ color: INK }}>
              {doc.number}
            </p>
            {doc.status && (
              <span
                className="mt-2 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                style={{ color: TONE[doc.status.tone].fg, backgroundColor: TONE[doc.status.tone].bg }}
              >
                {doc.status.label}
              </span>
            )}
            <div className="mt-3 space-y-0.5 text-[12px] leading-5" style={{ color: INK_MUTED }}>
              {doc.company.phone && <p>{doc.company.phone}</p>}
              {doc.company.email && <p>{doc.company.email}</p>}
              {doc.company.website && <p>{doc.company.website.replace(/^https?:\/\//, '')}</p>}
            </div>
          </div>
        </header>

        {/* Meta strip */}
        {doc.meta.length > 0 && (
          <section
            className="mt-7 grid gap-x-4 gap-y-3 rounded-lg px-5 py-4 avoid-break"
            style={{
              backgroundColor: brandTint,
              gridTemplateColumns: `repeat(${Math.min(doc.meta.length, 4)}, minmax(0, 1fr))`,
            }}
          >
            {doc.meta.map((m) => (
              <div key={m.label} className="min-w-0">
                <Eyebrow color={hexToRgba(brand, 0.9)}>{m.label}</Eyebrow>
                <p className="truncate text-[13px] font-semibold" style={{ color: INK }}>{m.value || '—'}</p>
              </div>
            ))}
          </section>
        )}

        {/* Parties */}
        <section className="mt-7 grid grid-cols-2 gap-8 avoid-break">
          <div>
            <Eyebrow>{doc.billTo.heading}</Eyebrow>
            <p className="text-[15px] font-semibold leading-6" style={{ color: INK }}>{doc.billTo.name}</p>
            {doc.billTo.lines.map((line, i) => (
              <p key={`${line}-${i}`} className="text-[12.5px] leading-5" style={{ color: INK_MUTED }}>{line}</p>
            ))}
            {(doc.billTo.phone || doc.billTo.email) && (
              <div className="mt-2">
                {doc.billTo.phone && <ContactLine label="Tel" value={doc.billTo.phone} href={`tel:${doc.billTo.phone}`} />}
                {doc.billTo.email && <ContactLine label="Email" value={doc.billTo.email} href={`mailto:${doc.billTo.email}`} />}
              </div>
            )}
          </div>
          {doc.serviceAddress && (
            <div>
              <Eyebrow>Service address</Eyebrow>
              <p className="whitespace-pre-line text-[12.5px] leading-5" style={{ color: INK_MUTED }}>
                {doc.serviceAddress}
              </p>
            </div>
          )}
        </section>

        {/* Scope */}
        {doc.scope && (
          <section className="mt-6 avoid-break">
            <Eyebrow>{doc.scopeHeading ?? 'Scope of work'}</Eyebrow>
            <p className="whitespace-pre-wrap text-[13px] leading-6" style={{ color: INK }}>
              {doc.scope}
            </p>
          </section>
        )}

        {/* Line items */}
        <section className="mt-7">
          <div className={cn(variant === 'screen' && 'overflow-x-auto -mx-1 px-1')}>
            <table className="w-full border-collapse text-[13px]" style={{ minWidth: variant === 'screen' ? 480 : undefined }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${brandRule}` }}>
                  <th className="pb-2 text-left text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: INK_FAINT }}>Description</th>
                  <th className="w-16 pb-2 text-right text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: INK_FAINT }}>Qty</th>
                  <th className="w-28 pb-2 text-right text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: INK_FAINT }}>Unit price</th>
                  <th className="w-28 pb-2 text-right text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: INK_FAINT }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {doc.lineItems.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-[12px]" style={{ color: INK_FAINT }}>
                      No line items
                    </td>
                  </tr>
                )}
                {doc.lineItems.map((item, i) => (
                  <tr
                    key={i}
                    className="avoid-break align-top"
                    style={{ borderBottom: `1px solid ${RULE}`, backgroundColor: i % 2 === 1 ? PAPER_TINT : 'transparent' }}
                  >
                    <td className="px-2 py-2.5 leading-5 first:pl-1" style={{ color: INK }}>
                      <span className="whitespace-pre-wrap">{item.description || '—'}</span>
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums" style={{ color: INK_MUTED }}>
                      {Number.isInteger(item.quantity) ? item.quantity : item.quantity.toFixed(2)}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums" style={{ color: INK_MUTED }}>{money(item.unitPrice)}</td>
                    <td className="py-2.5 pl-2 pr-1 text-right font-medium tabular-nums" style={{ color: INK }}>{money(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Totals */}
        <section className="mt-5 flex justify-end avoid-break">
          <div className="w-[300px] max-w-full">
            <div className="flex justify-between py-1.5 text-[13px]">
              <span style={{ color: INK_MUTED }}>Subtotal</span>
              <span className="tabular-nums" style={{ color: INK }}>{money(doc.totals.subtotal)}</span>
            </div>
            {doc.totals.tax && (
              <div className="flex justify-between py-1.5 text-[13px]">
                <span style={{ color: INK_MUTED }}>{doc.totals.tax.label}</span>
                <span className="tabular-nums" style={{ color: INK }}>{money(doc.totals.tax.amount)}</span>
              </div>
            )}
            <div
              className="mt-1.5 flex items-baseline justify-between rounded-md px-3 py-2.5"
              style={{ backgroundColor: showBalance ? PAPER_TINT : brandTint }}
            >
              <span className="text-[13px] font-semibold" style={{ color: INK }}>{doc.totals.totalLabel ?? 'Total'}</span>
              <span className="text-[18px] font-bold tabular-nums" style={{ color: showBalance ? INK : brand }}>
                {money(doc.totals.total)}
              </span>
            </div>
            {showBalance && (
              <>
                {paymentLines.map((line, i) => (
                  <div key={i} className="flex justify-between gap-3 px-3 py-1.5 text-[13px]">
                    <span style={{ color: TONE.success.fg }}>
                      {line.label}
                      {line.detail && <span style={{ color: INK_MUTED }}> · {line.detail}</span>}
                    </span>
                    <span className="whitespace-nowrap tabular-nums" style={{ color: TONE.success.fg }}>− {money(line.amount)}</span>
                  </div>
                ))}
                <div
                  className="flex items-baseline justify-between rounded-md px-3 py-2.5"
                  style={{ backgroundColor: isPaid ? TONE.success.bg : brandTint }}
                >
                  <span className="text-[13px] font-semibold" style={{ color: INK }}>Balance due</span>
                  <span className="text-[18px] font-bold tabular-nums" style={{ color: isPaid ? TONE.success.fg : brand }}>
                    {money(balanceDue)}
                  </span>
                </div>
              </>
            )}
            {doc.totals.note && (
              <p className="mt-2 text-right text-[10.5px] leading-4" style={{ color: INK_FAINT }}>{doc.totals.note}</p>
            )}
          </div>
        </section>

        {/* Payment */}
        {doc.payment && (
          <section
            className="mt-7 rounded-lg border px-5 py-4 avoid-break"
            style={{ borderColor: RULE, backgroundColor: PAPER_TINT }}
          >
            <Eyebrow color={brand}>{doc.payment.heading}</Eyebrow>
            {doc.payment.onlineUrl && (
              <p className="mb-3 text-[12.5px]" style={{ color: INK }}>
                <span className="font-semibold">{doc.payment.onlineLabel ?? 'Pay online'}:</span>{' '}
                <a href={doc.payment.onlineUrl} className="break-all" style={{ color: brand }}>
                  {doc.payment.onlineUrl}
                </a>
              </p>
            )}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              {doc.payment.fields.length > 0 && (
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12.5px]">
                  {doc.payment.fields.map((f) => (
                    <div key={f.label} className="contents">
                      <span style={{ color: INK_MUTED }}>{f.label}</span>
                      <span className="font-semibold tabular-nums" style={{ color: INK }}>{f.value}</span>
                    </div>
                  ))}
                  {doc.payment.referenceHint && (
                    <p className="col-span-2 mt-1 text-[11.5px]" style={{ color: INK_MUTED }}>{doc.payment.referenceHint}</p>
                  )}
                </div>
              )}
              {doc.payment.instructions && (
                <p className="whitespace-pre-wrap text-[12.5px] leading-5" style={{ color: INK_MUTED }}>
                  {doc.payment.instructions}
                </p>
              )}
            </div>
          </section>
        )}

        {/* Acceptance */}
        {doc.acceptance && (doc.acceptance.signed || doc.acceptance.howToAccept) && (
          <section
            className="mt-7 rounded-lg border px-5 py-4 avoid-break"
            style={{ borderColor: doc.acceptance.signed ? hexToRgba(TONE.success.fg, 0.35) : RULE, backgroundColor: doc.acceptance.signed ? TONE.success.bg : PAPER_TINT }}
          >
            {doc.acceptance.signed ? (
              <div className="flex items-start justify-between gap-6">
                <div>
                  <Eyebrow color={TONE.success.fg}>{doc.acceptance.heading || 'Accepted'}</Eyebrow>
                  <p className="text-[13px] font-semibold" style={{ color: INK }}>
                    {doc.acceptance.signerName || 'Customer'}
                  </p>
                  {doc.acceptance.signedAtLabel && (
                    <p className="text-[12px]" style={{ color: INK_MUTED }}>{doc.acceptance.signedAtLabel}</p>
                  )}
                  {doc.acceptance.auditLine && (
                    <p className="mt-1.5 text-[10.5px]" style={{ color: INK_FAINT }}>{doc.acceptance.auditLine}</p>
                  )}
                </div>
                {doc.acceptance.signatureImageUrl && (
                  <img
                    src={doc.acceptance.signatureImageUrl}
                    alt="Customer signature"
                    className="h-16 max-w-[220px] rounded bg-white object-contain px-2"
                    style={{ border: `1px solid ${RULE}` }}
                  />
                )}
              </div>
            ) : (
              <>
                <Eyebrow color={brand}>Accepting this quotation</Eyebrow>
                <p className="text-[12.5px] leading-5" style={{ color: INK_MUTED }}>{doc.acceptance.howToAccept}</p>
              </>
            )}
          </section>
        )}

        {/* Free-text sections */}
        {doc.sections.length > 0 && (
          <section className="mt-7 space-y-4">
            {doc.sections.map((s) => (
              <div key={s.heading} className="avoid-break">
                <Eyebrow>{s.heading}</Eyebrow>
                <p className="whitespace-pre-wrap text-[11.5px] leading-5" style={{ color: INK_MUTED }}>{s.body}</p>
              </div>
            ))}
          </section>
        )}

        {/* Footer */}
        <footer
          className="mt-9 flex items-end justify-between gap-6 border-t pt-4 avoid-break"
          style={{ borderColor: RULE }}
        >
          <p className="text-[12px] font-medium" style={{ color: INK }}>{doc.footer.thanks}</p>
          <div className="text-right text-[10px] leading-4" style={{ color: INK_FAINT }}>
            <p>{doc.footer.legalLine}</p>
            <p>{doc.footer.currencyLine}</p>
          </div>
        </footer>
      </div>
    );
  },
);

DocumentSheet.displayName = 'DocumentSheet';
